import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  signal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  HicapsConnectMethod,
  PrincipleHicapsConnectRequest,
} from '@principle-theorem/hicaps-connect';
import {
  HicapsConnectService,
  HicapsConnectV2MerchantSeedService,
} from '@principle-theorem/ng-payments';
import {
  DialogPresets,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import { Practice } from '@principle-theorem/principle-core';
import {
  IHicapsConnectTerminal,
  IPractice,
  IPracticeHicapsConnectSettings,
} from '@principle-theorem/principle-core/interfaces';
import { WithRef, patchDoc, snapshot } from '@principle-theorem/shared';
import { HicapsConnectHelpers } from 'libs/ng-payments/src/lib/transactions/transaction-providers/hicaps-connect/hicaps-connect-helpers';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';
import {
  HicapsCustomRequestComponent,
  IHicapsConnectJsonRequest,
} from './hicaps-custom-request/hicaps-custom-request.component';
import { HICAPS_SETTINGS_NOTICE_MS } from './practice-hicaps-connect-v2-settings/hicaps-connect-v2-settings-notice';

/**
 * Notices that ask the user to do something next stay up longer than the
 * plain confirmations covered by `HICAPS_SETTINGS_NOTICE_MS`, so the
 * instruction is still on screen when they look for what to do.
 */
export const HICAPS_ACTION_REQUIRED_NOTICE_MS = 5000;

@Component({
  selector: 'pr-practice-hicaps-connect-settings',
  templateUrl: './practice-hicaps-connect-settings.component.html',
  styleUrls: ['./practice-hicaps-connect-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PracticeHicapsConnectSettingsComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  practice$ = new ReplaySubject<WithRef<IPractice>>(1);
  terminals$: Observable<WithRef<IHicapsConnectTerminal>[]>;
  useV2Terminals$: Observable<boolean>;
  readonly isSeeding = signal(false);
  settingsForm = new TypedFormGroup<IPracticeHicapsConnectSettings>({
    isEnabled: new TypedFormControl<boolean>(false),
    testModeEnabled: new TypedFormControl<boolean>(false),
    addCDBSAsPending: new TypedFormControl<boolean>(false),
    useV2Terminals: new TypedFormControl<boolean>(false),
  });

  @Input()
  set practice(practice: WithRef<IPractice>) {
    if (practice) {
      this.practice$.next(practice);
    }
  }

  constructor(
    private _hicapsConnect: HicapsConnectService,
    private _dialog: MatDialog,
    private _seedService: HicapsConnectV2MerchantSeedService,
    private _snackBar: MatSnackBar
  ) {
    this.practice$
      .pipe(
        take(1),
        map((practice) => practice.hicapsConnectSettings),
        takeUntil(this._onDestroy$)
      )
      .subscribe((settings) => {
        if (settings) {
          this.settingsForm.patchValue(settings ?? {});
        }
      });

    this.terminals$ = this.practice$.pipe(
      switchMap((practice) => Practice.hicapsConnectTerminals$(practice))
    );

    this.useV2Terminals$ = this.practice$.pipe(
      map((practice) => Practice.useV2Terminals(practice))
    );
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  async submit(): Promise<void> {
    const practice = await snapshot(this.practice$);
    const hicapsConnectSettings = this.settingsForm.value;
    await patchDoc(practice.ref, { hicapsConnectSettings });
    this.practice$.next({ ...practice, hicapsConnectSettings });
  }

  async seedMerchants(practice: WithRef<IPractice>): Promise<void> {
    try {
      this.isSeeding.set(true);
      await this._seedService.seedMerchantsFromV1(practice);
      this._snackBar.open('Merchants detected. Please run Sync now.', 'OK', {
        duration: HICAPS_ACTION_REQUIRED_NOTICE_MS,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        `Unable to detect HICAPS merchants for practice ${practice.ref.id}`,
        error
      );
      this._snackBar.open('Failed to detect merchants', 'OK', {
        duration: HICAPS_SETTINGS_NOTICE_MS,
      });
    } finally {
      this.isSeeding.set(false);
    }
  }

  async customRequest(): Promise<void> {
    const practice = await snapshot(this.practice$);
    const invoice = {
      practice,
      items: [],
      claims: [],
    };
    const terminal = await this._hicapsConnect.selectTerminal(invoice);
    if (!terminal) {
      return;
    }
    const baseRequest = HicapsConnectHelpers.buildBaseRequest(
      this._hicapsConnect.getConfig(),
      terminal
    );
    const request = await this._dialog
      .open<
        HicapsCustomRequestComponent,
        Partial<IHicapsConnectJsonRequest> | undefined,
        IHicapsConnectJsonRequest | undefined
      >(
        HicapsCustomRequestComponent,
        DialogPresets.fullscreen({ data: { request: baseRequest } })
      )
      .afterClosed()
      .toPromise();

    // eslint-disable-next-line no-console
    console.log('Submitting customRequest', { request });
    if (!request) {
      return;
    }

    const api = this._hicapsConnect.getDeviceAPI(terminal.bridgeDevice);

    // Pretend to be a quote request, but this data can be whatever you want to test
    const response = await snapshot(
      api.call$<HicapsConnectMethod.SendQuoteRequest>(
        request.methodName as HicapsConnectMethod.SendQuoteRequest,
        request.request as unknown as PrincipleHicapsConnectRequest<HicapsConnectMethod.SendQuoteRequest>['data'],
        request.extendedData as unknown as PrincipleHicapsConnectRequest<HicapsConnectMethod.SendQuoteRequest>['extendedData']
      )
    );

    // eslint-disable-next-line no-console
    console.log('Received customRequest', { request, response });
  }
}
